import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { ArrowDownUp, Barcode, ClipboardCheck, History, PackageSearch, Printer, SlidersHorizontal } from 'lucide-react';
import {
  MOVEMENT_REASON_LABELS, formatDateTime, formatMoney, formatQty, toNumber, type Paginated
} from '@erp/shared';
import { api, openPdf } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useDebounced } from '@/hooks/useDebounced';
import { PageHeader, SearchInput, ErrorState, EmptyState, StatCard } from '@/components/ui/misc';
import { Button } from '@/components/ui/button';
import { Badge, StatusBadge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { DataTable, Pagination, type Column } from '@/components/ui/table';
import { InlineView } from '@/components/ui/inline-view';
import { Field, Input, Select, Textarea } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';

interface StockRow {
  id: string; sku: string; name: string; unit_code: string; unit_kind: string;
  physical: number; reserved: number; incoming: number; available: number; projected: number;
  min_stock: number; target_stock: number; safety_stock: number; lead_time_days: number; supplier: string | null;
}

interface MovementRow {
  id: number; quantity: number; reason: string; reference_type: string | null; reference_id: string | null;
  note: string | null; created_at: string; unit_cost: number | null; sku: string; product_name: string;
  unit_code: string; location_code: string; user_name: string | null; terminal_code: string | null;
}

interface LocationRow { id: string; code: string; name: string; warehouse_name: string }

const MOVEMENT_OPTIONS = ['adjustment', 'initial', 'return', 'scrap'] as const;

export function StockPage() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState('stock');
  // `?q=SKU` llega desde las alertas del cuadro de mando: abre el stock ya buscando eso.
  const [search, setSearch] = useState(params.get('q') ?? '');
  // La URL manda al entrar y recoge lo que se escribe: recargar no pierde la búsqueda.
  useEffect(() => {
    const enUrl = params.get('q') ?? '';
    setSearch((actual) => (actual === enUrl ? actual : enUrl));
  }, [params]);
  const debounced = useDebounced(search, 300);
  const [lowStock, setLowStock] = useState(params.get('lowStock') === '1');
  useEffect(() => {
    const enUrl = params.get('q') ?? '';
    if (debounced === enUrl) return;
    setParams((current) => {
      if (debounced) current.set('q', debounced);
      else current.delete('q');
      return current;
    }, { replace: true });
  }, [debounced, params, setParams]);

  const [page, setPage] = useState(1);
  const [movePage, setMovePage] = useState(1);
  const [adjusting, setAdjusting] = useState<StockRow | null>(null);
  const [inventory, setInventory] = useState<StockRow | null>(null);
  const [labelCopies, setLabelCopies] = useState('1');

  useEffect(() => { setPage(1); }, [debounced, lowStock]);

  const stockQueryParams = new URLSearchParams({ page: String(page), pageSize: '25' });
  if (debounced) stockQueryParams.set('q', debounced);
  if (lowStock) stockQueryParams.set('lowStock', 'true');

  const stockQuery = useQuery({
    queryKey: ['stock', page, debounced, lowStock],
    queryFn: () => api<Paginated<StockRow>>(`/stock?${stockQueryParams.toString()}`)
  });

  const movementsQuery = useQuery({
    queryKey: ['movements', movePage],
    queryFn: () => api<Paginated<MovementRow>>(`/stock/movements?page=${movePage}&pageSize=30`)
  });

  const locationsQuery = useQuery({
    queryKey: ['locations'],
    queryFn: () => api<{ data: LocationRow[] }>('/locations')
  });

  const rows = stockQuery.data?.data ?? [];
  const lowCount = rows.filter((row) => row.available <= row.min_stock).length;
  const inStock = rows.filter((row) => row.available > 0).length;

  const printFilteredLabels = () => {
    const params = new URLSearchParams({ storable: 'true', copies: labelCopies, limit: '1000' });
    if (debounced) params.set('q', debounced);
    if (lowStock) params.set('lowStock', 'true');
    void openPdf(`/products/labels.pdf?${params.toString()}`)
      .catch((error) => toast.error((error as Error).message));
  };

  const printEveryProductLabel = () => {
    // Todos los artículos activos, incluidos los no almacenados.
    void openPdf(`/products/labels.pdf?all=true&copies=${labelCopies}`)
      .catch((error) => toast.error((error as Error).message));
  };

  const stockColumns: Column<StockRow>[] = [
    {
      key: 'product',
      header: 'Artículo',
      cell: (row) => (
        <div className="min-w-[200px]">
          <p className="font-medium">{row.name}</p>
          <p className="font-mono text-[10px] text-muted-foreground">{row.sku}</p>
        </div>
      )
    },
    { key: 'available', header: 'Disponible', cell: (row) => <Badge tone={row.available <= 0 ? 'danger' : row.available <= row.min_stock ? 'warning' : 'success'}>{formatQty(row.available)} {row.unit_code}</Badge> },
    { key: 'physical', header: 'Físico', cell: (row) => <span className="tabular-nums">{formatQty(row.physical)}</span> },
    { key: 'reserved', header: 'Reservado', cell: (row) => <span className="tabular-nums text-muted-foreground">{formatQty(row.reserved)}</span> },
    { key: 'incoming', header: 'Entrante', cell: (row) => <span className="tabular-nums text-muted-foreground">{formatQty(row.incoming)}</span> },
    { key: 'projected', header: 'Proyectado', cell: (row) => <span className={`font-medium tabular-nums ${row.projected < row.min_stock ? 'text-amber-600' : ''}`}>{formatQty(row.projected)}</span> },
    { key: 'min', header: 'Mínimo', cell: (row) => <span className="tabular-nums text-muted-foreground">{formatQty(row.min_stock)}</span> },
    { key: 'lead', header: 'Lead time', cell: (row) => <span className="text-muted-foreground">{row.lead_time_days} d</span> },
    {
      key: 'actions',
      header: '',
      cell: (row) => (
        <div className="flex justify-end gap-1">
          {can('products.read') ? (
            <Button
              variant="ghost"
              size="icon-sm"
              title="Imprimir código de barras"
              onClick={(event) => {
                event.stopPropagation();
                void openPdf(`/products/${row.id}/label.pdf?copies=${labelCopies}`)
                  .catch((error) => toast.error((error as Error).message));
              }}
            >
              <Barcode />
            </Button>
          ) : null}
          {can('stock.move') ? (
            <>
              <Button variant="ghost" size="icon-sm" title="Ajustar" onClick={(event) => { event.stopPropagation(); setAdjusting(row); }}><ArrowDownUp /></Button>
              <Button variant="ghost" size="icon-sm" title="Inventario" onClick={(event) => { event.stopPropagation(); setInventory(row); }}><ClipboardCheck /></Button>
            </>
          ) : null}
        </div>
      )
    }
  ];

  const movementColumns: Column<MovementRow>[] = [
    { key: 'date', header: 'Fecha', cell: (row) => <span className="text-[11px] text-muted-foreground">{formatDateTime(row.created_at)}</span> },
    { key: 'product', header: 'Artículo', cell: (row) => <div><p className="font-medium">{row.product_name}</p><p className="font-mono text-[10px] text-muted-foreground">{row.sku}</p></div> },
    { key: 'reason', header: 'Motivo', cell: (row) => <Badge tone="outline">{MOVEMENT_REASON_LABELS[row.reason as keyof typeof MOVEMENT_REASON_LABELS] ?? row.reason}</Badge> },
    { key: 'qty', header: 'Cantidad', cell: (row) => <span className={`font-semibold tabular-nums ${row.quantity < 0 ? 'text-red-600' : 'text-emerald-600'}`}>{row.quantity > 0 ? '+' : ''}{formatQty(row.quantity)} {row.unit_code}</span> },
    { key: 'location', header: 'Ubicación', cell: (row) => <span className="font-mono text-[11px]">{row.location_code}</span> },
    { key: 'reference', header: 'Referencia', cell: (row) => <span className="text-[10px] text-muted-foreground">{[row.reference_type, row.reference_id].filter(Boolean).join(' · ') || '—'}</span> },
    { key: 'user', header: 'Usuario', cell: (row) => <span className="text-[11px] text-muted-foreground">{row.user_name ?? 'Sistema'}{row.terminal_code ? ` · ${row.terminal_code}` : ''}</span> },
    { key: 'note', header: 'Nota', cell: (row) => <span className="max-w-48 truncate text-[11px] text-muted-foreground">{row.note ?? '—'}</span> }
  ];

  return (
    <div>
      <div className={adjusting || inventory ? 'hidden' : ''}>
      <PageHeader
        title="Stock y movimientos"
        description="Disponible = físico − reservado · Proyectado = disponible + compras entrantes"
        actions={(
          <>
            <label className="flex items-center gap-1 text-[11px] text-muted-foreground">
              Copias
              <Input
                type="number"
                min="1"
                max="10"
                value={labelCopies}
                onChange={(event) => setLabelCopies(event.target.value.replace(/\D/g, '').slice(0, 2) || '1')}
                className="h-9 w-14 text-center"
                title="Copias por artículo"
              />
            </label>
            <Button onClick={printEveryProductLabel} title="Genera un PDF A4 con el EAN-13 de todos los artículos activos, sin filtros">
              <Barcode /> Imprimir todas las etiquetas
            </Button>
            <Button variant="outline" onClick={printFilteredLabels} title="Genera un PDF A4 con el EAN-13 de los artículos del filtro actual">
              <Printer /> Imprimir filtro
            </Button>
          </>
        )}
      />

      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <StatCard label="Artículos mostrados" value={rows.length} hint="Página actual" icon={PackageSearch} />
        <StatCard label="Bajo mínimo" value={lowCount} hint="En la página actual" icon={SlidersHorizontal} tone={lowCount ? 'warning' : 'success'} />
        <StatCard label="Con stock" value={inStock} hint="Artículos de la página actual" icon={History} tone="neutral" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="stock">Existencias</TabsTrigger>
          <TabsTrigger value="movements">Movimientos</TabsTrigger>
        </TabsList>

        <TabsContent value="stock">
          <Card>
            <div className="flex flex-wrap items-center gap-2 border-b border-border p-3">
              <SearchInput value={search} onChange={setSearch} placeholder="Buscar artículo…" className="w-full sm:w-72" />
              <Button variant={lowStock ? 'default' : 'outline'} size="sm" onClick={() => setLowStock((value) => !value)}>Bajo mínimo</Button>
            </div>
            {stockQuery.error ? <ErrorState message={(stockQuery.error as Error).message} onRetry={() => void stockQuery.refetch()} /> : (
              <DataTable
                columns={stockColumns}
                rows={rows}
                loading={stockQuery.isLoading}
                rowKey={(row) => row.id}
                empty={<EmptyState title="Sin artículos" description="Ajusta los filtros o crea artículos en el catálogo." />}
                footer={<Pagination page={page} totalPages={stockQuery.data?.meta.totalPages ?? 1} total={stockQuery.data?.meta.total ?? 0} onPageChange={setPage} />}
              />
            )}
          </Card>
        </TabsContent>

        <TabsContent value="movements">
          <Card>
            <div className="flex items-center justify-between border-b border-border p-3">
              <p className="text-xs text-muted-foreground">Registro inmutable de entradas, consumos y regularizaciones.</p>
              <Button variant="outline" size="sm" onClick={() => void movementsQuery.refetch()}>Actualizar</Button>
            </div>
            {movementsQuery.error ? <ErrorState message={(movementsQuery.error as Error).message} onRetry={() => void movementsQuery.refetch()} /> : (
              <DataTable
                columns={movementColumns}
                rows={movementsQuery.data?.data ?? []}
                loading={movementsQuery.isLoading}
                rowKey={(row) => String(row.id)}
                empty={<EmptyState title="Sin movimientos" />}
                footer={<Pagination page={movePage} totalPages={movementsQuery.data?.meta.totalPages ?? 1} total={movementsQuery.data?.meta.total ?? 0} onPageChange={setMovePage} />}
              />
            )}
          </Card>
        </TabsContent>
      </Tabs>
      </div>

      <MovementModal
        open={Boolean(adjusting)}
        onOpenChange={(open) => { if (!open) setAdjusting(null); }}
        product={adjusting}
        locations={locationsQuery.data?.data ?? []}
        onSaved={() => {
          void qc.invalidateQueries({ queryKey: ['stock'] });
          void qc.invalidateQueries({ queryKey: ['movements'] });
          void qc.invalidateQueries({ queryKey: ['dashboard'] });
          setAdjusting(null);
        }}
      />

      <InventoryModal
        open={Boolean(inventory)}
        onOpenChange={(open) => { if (!open) setInventory(null); }}
        product={inventory}
        locations={locationsQuery.data?.data ?? []}
        onSaved={() => {
          void qc.invalidateQueries({ queryKey: ['stock'] });
          void qc.invalidateQueries({ queryKey: ['movements'] });
          setInventory(null);
        }}
      />
    </div>
  );
}

function MovementModal({ open, onOpenChange, product, locations, onSaved }: {
  open: boolean; onOpenChange: (open: boolean) => void; product: StockRow | null;
  locations: LocationRow[]; onSaved: () => void;
}) {
  const [locationId, setLocationId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('adjustment');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setQuantity('');
      setReason('adjustment');
      setNote('');
      setLocationId(locations[0]?.id ?? '');
    }
  }, [open, locations]);

  const submit = async () => {
    if (!product) return;
    setSaving(true);
    try {
      const value = Number(quantity);
      if (!value) throw new Error('Indica una cantidad distinta de cero');
      await api('/stock/movements', {
        method: 'POST',
        body: { productId: product.id, locationId, quantity: value, reason, note: note || null }
      });
      toast.success('Movimiento registrado');
      onSaved();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <InlineView
      open={open} onOpenChange={onOpenChange} title="Movimiento manual de stock"
      description={product ? `${product.name} · ${product.sku}` : undefined} size="sm"
      footer={<><Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button><Button loading={saving} onClick={() => void submit()} disabled={!locationId}>Registrar</Button></>}
    >
      <div className="space-y-3">
        <Field label="Ubicación" required>
          <Select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
            {locations.map((location) => <option key={location.id} value={location.id}>{location.code} · {location.name}</option>)}
          </Select>
        </Field>
        <Field label="Cantidad" required hint="Positivo entra, negativo consume. Queda auditado.">
          <Input type="number" step="0.001" value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="Ej.: 10 o -2.5" />
        </Field>
        <Field label="Motivo">
          <Select value={reason} onChange={(e) => setReason(e.target.value)}>
            {MOVEMENT_OPTIONS.map((option) => <option key={option} value={option}>{MOVEMENT_REASON_LABELS[option]}</option>)}
          </Select>
        </Field>
        <Field label="Nota"><Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} /></Field>
      </div>
    </InlineView>
  );
}

function InventoryModal({ open, onOpenChange, product, locations, onSaved }: {
  open: boolean; onOpenChange: (open: boolean) => void; product: StockRow | null;
  locations: LocationRow[]; onSaved: () => void;
}) {
  const [locationId, setLocationId] = useState('');
  const [counted, setCounted] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setCounted('');
      setNote('');
      setLocationId(locations[0]?.id ?? '');
    }
  }, [open, locations]);

  const submit = async () => {
    if (!product) return;
    setSaving(true);
    try {
      await api('/stock/inventory', {
        method: 'POST',
        body: { productId: product.id, locationId, countedQuantity: Number(counted), note: note || undefined }
      });
      toast.success('Inventario regularizado');
      onSaved();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <InlineView
      open={open} onOpenChange={onOpenChange} title="Recuento de inventario"
      description={product ? `${product.name} · stock actual ${formatQty(product.physical)} ${product.unit_code}` : undefined} size="sm"
      footer={<><Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button><Button loading={saving} onClick={() => void submit()} disabled={!locationId || counted === ''}>Regularizar</Button></>}
    >
      <div className="space-y-3">
        <Field label="Ubicación" required>
          <Select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
            {locations.map((location) => <option key={location.id} value={location.id}>{location.code} · {location.name}</option>)}
          </Select>
        </Field>
        <Field label="Cantidad contada" required hint="Se registra la diferencia como regularización auditada.">
          <Input type="number" step="0.001" min="0" value={counted} onChange={(e) => setCounted(e.target.value)} />
        </Field>
        <Field label="Observaciones"><Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} /></Field>
      </div>
    </InlineView>
  );
}
